import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/atoms/Card';
import ContentBlock from '@/components/molecules/ContentBlock';
import type {
    LocationProviderConfig,
    PenRef,
    ProviderGroup,
    ProviderSchema,
} from '@/domain/providerConfig';
import { FieldContexts } from '@/routes/shared/ProviderConfig/ProviderSchemaField';

import type { FormDiff } from '../form/formDiff';
import { onlyDeactivatesPens } from '../form/formDiff';
import { missesRequiredForTest } from '../form/testReadiness';
import { useCatalogLoader } from '../hooks/useCatalogLoader';
import { useHeldBackFields } from '../hooks/useHeldBackFields';
import { useProviderCardForm } from '../hooks/useProviderCardForm';
import { useTestPens } from '../hooks/useTestPens';

import { CardFooter, scrollToFirstProblem } from './CardFooter';
import { CardMessages } from './CardMessages';
import { ConnectionSection } from './ConnectionSection';
import { CredentialsSection } from './CredentialsSection';
import { FetchHistoryModal } from './FetchHistoryModal';
import { PenMappingTable } from './PenMappingTable';
import { SaveConfirmModal } from './SaveConfirmModal';
import { StaleBanner } from './StaleBanner';
import { showSaveOutcomeToast } from './saveToasts';
import { TestControls } from './TestControls';
import { TestResultPanel } from './TestResultPanel';
import { useCardHeader } from './useCardHeader';

import styles from './ProviderCard.module.scss';

export interface ProviderCardProps {
    schema: ProviderSchema;
    group: ProviderGroup | null;
    pens: PenRef[];
    locationId: number;
    isOptoScaleAdmin: boolean;
    refetch: () => Promise<LocationProviderConfig | null>;
    onDirtyChange: (providerName: string, dirty: boolean) => void;
}

/** One provider at the site: its settings, the connection test and the save. */
function ProviderCard(props: ProviderCardProps) {
    const { schema, pens, locationId, isOptoScaleAdmin, onDirtyChange } = props;
    const { providerName } = schema;
    const { t } = useTranslation();
    const { heldBack, fields: heldBackFields } = useHeldBackFields();
    // The catalog loader is made after the form it watches, and hears of its saves here.
    const onSaved = useRef<(saved: FormDiff) => void>(() => undefined);
    const relay = (saved: FormDiff): void => onSaved.current(saved);
    const card = useProviderCardForm({ ...props, providerName, heldBack, onSaved: relay });
    const { form } = card;
    const catalog = useCatalogLoader(locationId, card.baseGroup, card.diff);
    onSaved.current = catalog.keepThroughSave;
    const containerRef = useRef<HTMLDivElement>(null);
    const [confirming, setConfirming] = useState(false);
    const [reloading, setReloading] = useState(false);
    const [historyPen, setHistoryPen] = useState<PenRef | null>(null);
    // Save is disabled after a save and must not get focus back over the field to fix after
    // a refused one, so the dialog hands focus back only when the save failed otherwise.
    const [focusAfterClose, setFocusAfterClose] = useState<'save' | 'problem' | 'heading'>('save');
    const titleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        onDirtyChange(providerName, card.dirty);
        // A card that goes away, e.g. once its last pen is removed and saved, holds no edits.
        return () => onDirtyChange(providerName, false);
    }, [onDirtyChange, providerName, card.dirty]);

    const testPicker = useTestPens(pens, form.getValues('pens'), card.baseGroup);
    const testPens = testPicker.selected;
    const missingRequired = missesRequiredForTest(card.clientErrors, testPens);
    const header = useCardHeader(card.baseGroup);

    async function confirmSave(): Promise<void> {
        // Read before the save, which starts the form over from the saved rows.
        const onlyDeactivated = onlyDeactivatesPens(card.diff);
        const outcome = await card.save();
        if (outcome === 'saved') setFocusAfterClose('heading');
        if (outcome === 'invalid') setFocusAfterClose('problem');
        setConfirming(false);
        showSaveOutcomeToast(t, providerName, outcome, onlyDeactivated);
    }

    async function reload(): Promise<void> {
        setReloading(true);
        await card.reloadKeepingEdits();
        setReloading(false);
    }

    return (
        <ContentBlock
            title={t(`providerConfig.providers.${providerName}`)}
            {...header}
            containerRef={containerRef}
            titleRef={titleRef}>
            <FieldContexts heldBack={heldBackFields} clientAdmin={!isOptoScaleAdmin}>
                <form
                    className={styles.card}
                    noValidate
                    onSubmit={(event) => event.preventDefault()}>
                    <StaleBanner
                        stale={card.stale}
                        reloading={reloading}
                        onReload={() => void reload()}
                    />
                    <ConnectionSection
                        schema={schema}
                        group={card.baseGroup}
                        pens={pens}
                        control={form.control}
                        setValue={form.setValue}
                        conflicts={card.conflicts}
                    />
                    <CredentialsSection
                        schema={schema}
                        group={card.baseGroup}
                        control={form.control}
                    />
                    <PenMappingTable
                        schema={schema}
                        secretOutlook={card.secretOutlook}
                        setValue={form.setValue}
                        group={card.baseGroup}
                        pens={pens}
                        control={form.control}
                        isOptoScaleAdmin={isOptoScaleAdmin}
                        catalog={catalog.catalog}
                        catalogMessage={catalog.message}
                        onLoadCatalog={() => void catalog.load()}
                        onFetchHistory={setHistoryPen}
                        conflicts={card.conflicts}
                    />
                    {/* One panel: the controls and the result they give read as one step. */}
                    <Card variant="subtle" radius="sm" className={styles.testPanel}>
                        <TestControls
                            candidates={testPicker.candidates}
                            selected={testPens}
                            onSelectedChange={testPicker.onSelectedChange}
                            onRun={() => void card.runTest(testPens)}
                            testing={card.test.testing}
                            missingRequired={missingRequired}
                            needsTest={card.needsTest}
                            tokenValid={card.test.tokenValid}
                            showOverride={isOptoScaleAdmin}
                            testOverride={card.testOverride}
                            onTestOverrideChange={card.setTestOverride}
                        />
                        <TestResultPanel
                            result={card.test.result}
                            stale={card.test.stale}
                            pens={pens}
                        />
                    </Card>
                    <CardMessages
                        errors={{ save: card.saveError, test: card.test.error }}
                        pens={pens}
                    />
                    <CardFooter
                        blocker={card.saveBlockedBy}
                        saving={card.saving}
                        onSave={() => setConfirming(true)}
                        cardRef={containerRef}
                    />
                </form>
            </FieldContexts>
            <SaveConfirmModal
                isOpen={confirming}
                summary={card.summary}
                saving={card.saving}
                onConfirm={() => void confirmSave()}
                onCancel={() => setConfirming(false)}
                onClosed={() => {
                    if (focusAfterClose === 'save') return;
                    setFocusAfterClose('save');
                    if (focusAfterClose === 'problem') scrollToFirstProblem(containerRef.current);
                    else titleRef.current?.focus();
                }}
            />
            {historyPen !== null && (
                <FetchHistoryModal
                    pen={historyPen}
                    locationId={locationId}
                    onClose={() => setHistoryPen(null)}
                />
            )}
        </ContentBlock>
    );
}

export default ProviderCard;
export { ProviderCard };
